// Hotel video upload, browser side (PLAN.md §G, §32). UI-free; network and storage are injected so the whole flow
// is unit-tested without a browser. Parts go straight to S3 with presigned URLs; every part carries its SHA-256 as
// a signed header, so S3 rejects other bytes.
//
// Resume: a session is remembered per (company access, file fingerprint). When the same file is picked again, the
// SHA-256 of every part S3 already holds is recomputed from the file and compared — any mismatch (another file
// with the same name/size/date) aborts that session and starts fresh, so bytes of two files never mix.
//
// Multi-upload hardening (PLAN-multivideo.md §5a): a failed part drains all other part uploads before the call
// returns, cancelling stops every phase, checksums are hashed one part at a time (memory on phones), errors keep
// their origin (status, server code, Retry-After), and a finished upload stays remembered for 24 h (doneAt) so
// picking the same file again does not upload it twice.

export const VIDEO_UPLOAD_MAX_BYTES = 1024 * 1024 * 1024
export const VIDEO_UPLOAD_MAX_SECONDS = 180
export const VIDEO_UPLOAD_TOLERANCE_SECONDS = 5
export const VIDEO_ACCEPTED_TYPES = ["video/mp4", "video/quicktime", "video/webm"] as const
const PARALLEL = 3
const URL_BATCH = 20
const PART_ATTEMPTS = 5
const COMPLETE_ROUNDS = 3
/** A finished upload is remembered this long; picking the same file again within it reports "already uploaded". */
export const VIDEO_DONE_TTL_MS = 24 * 3600 * 1000

export type SessionView = {
  id: string
  status: string
  partSize: number
  partCount: number
  size: number
  uploadedParts: { n: number; size: number; sha256: string | null }[]
  missingParts?: number[]
  errorCode: string | null
}

export type PartUrl = { n: number; url: string; headers: Record<string, string> }

export type VideoUploadPhase = "admit" | "resume" | "parts" | "complete" | "discard"

export type VideoUploadErrorDetail = {
  /** HTTP status of the failing app request (0 = network). */
  status?: number
  /** Seconds the server asked to wait (Retry-After / RateLimit-Reset). */
  retryAfterSec?: number
  phase?: VideoUploadPhase
  /** The underlying code when `code` is a generic wrapper (e.g. resume_retry ← http_429). */
  cause?: string
}

export class VideoUploadError extends Error {
  constructor(public readonly code: string, message: string, public readonly detail: VideoUploadErrorDetail = {}) {
    super(message)
    this.name = "VideoUploadError"
  }
}

const cancelled = () => new VideoUploadError("cancelled", "Upload abgebrochen.")
const throwIfAborted = (signal?: AbortSignal) => { if (signal?.aborted) throw cancelled() }

export type VideoUploadDeps = {
  /** JSON request against the app; throws VideoUploadError(code) for non-2xx with a {code,error} body. */
  api: <T>(method: "GET" | "POST", path: string, body?: unknown, signal?: AbortSignal) => Promise<T>
  /** PUT one part to S3; resolves on 2xx; rejects with {status} otherwise. Reports bytes sent of this part. */
  putPart: (url: string, headers: Record<string, string>, body: Blob, onBytes: (sent: number) => void, signal?: AbortSignal) => Promise<void>
  storage: { get(key: string): string | null; set(key: string, value: string): void; remove(key: string): void; keys?(): string[] }
  sha256Base64: (blob: Blob) => Promise<string>
  uuid: () => string
  /** Resolves after `ms`, or early when the signal aborts. */
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>
  /** Resolves when the browser is back online, or early when the signal aborts. */
  waitOnline: (signal?: AbortSignal) => Promise<void>
}

export type VideoUploadInput = {
  file: File
  /** "/api/upload/video" (signed in) or "/api/upload/<token>/video" (upload link) */
  basePath: string
  /** Scopes remembered sessions: company access (user id, or a hash of the link token). */
  accessKey: string
  message?: string
  pillarName?: string
  durationSec?: number
  onProgress?: (p: { sentBytes: number; totalBytes: number }) => void
  signal?: AbortSignal
}

/** alreadyDone: the session was already uploaded when this call started (same file picked again). */
export type VideoUploadResult = { sessionId: string; status: string; alreadyDone: boolean }

/** Client-side check before anything is sent (PLAN §30). null = fine, else a German message. */
export function precheckVideo(file: { type: string; size: number }, durationSec?: number | null): string | null {
  if (!(VIDEO_ACCEPTED_TYPES as readonly string[]).includes(file.type)) return "Bitte ein MP4-, MOV- oder WebM-Video wählen."
  if (file.size <= 0) return "Die Datei ist leer."
  if (file.size > VIDEO_UPLOAD_MAX_BYTES) return "Datei zu groß (max. 1 GB)."
  if (durationSec != null && durationSec > VIDEO_UPLOAD_MAX_SECONDS + VIDEO_UPLOAD_TOLERANCE_SECONDS) return "Video zu lang (max. 3 Min.)."
  return null
}

const storageKey = (accessKey: string, f: File) => `hotel-video:${accessKey}:${f.name}:${f.size}:${f.lastModified}`
/** Same identity as the resume pointer; used as the cross-tab lock name for this file. */
export const videoFileKey = (accessKey: string, f: Pick<File, "name" | "size" | "lastModified">) =>
  `hotel-video:${accessKey}:${f.name}:${f.size}:${f.lastModified}`

function partBlob(file: Blob, view: Pick<SessionView, "partSize" | "partCount" | "size">, n: number): Blob {
  const start = (n - 1) * view.partSize
  const end = n === view.partCount ? view.size : start + view.partSize
  return file.slice(start, end)
}

function newClientUploadId(deps: VideoUploadDeps): string {
  return deps.uuid()
}

const PENDING_POLLS = 10
const PENDING_POLL_MS = 3000
const RETRY_LATER = "Verbindung prüfen und erneut versuchen, der Upload setzt dann fort."
const DEFINITIVE_REFUSALS = new Set([
  "client_id_conflict", "session_closed", "daily_limit", "too_many_open", "busy", "invalid_input", "unsupported_type", "too_long",
])

type Remembered = { sessionId?: string; clientUploadId: string; doneAt?: number }

const DONE_STATUSES = ["uploaded", "processing", "ready"]

const detailOf = (err: unknown, phase: VideoUploadPhase): VideoUploadErrorDetail => {
  if (err instanceof VideoUploadError) return { ...err.detail, phase, cause: err.detail.cause ?? err.code }
  return { status: 0, phase, cause: "network" }
}

/** Only a definitive "this session does not exist (for you)" may drop the resume pointer. */
const isGone = (err: unknown) => {
  const code = (err as { code?: string })?.code
  return code === "http_404" || code === "not_found"
}

/**
 * Reuse a remembered session only when every part S3 holds has the same SHA-256 as the file in hand.
 * The pointer is kept on any transient failure (offline, 429, 5xx) and while the server is still settling the
 * session (reserved, completing), so a retry resumes instead of admitting a duplicate (review video-r1 M4).
 */
async function resumeOrForget(
  deps: VideoUploadDeps, input: VideoUploadInput, key: string,
): Promise<{ view: SessionView; clientUploadId: string } | { pendingClientUploadId: string } | null> {
  const raw = deps.storage.get(key)
  if (!raw) return null
  let saved: Remembered
  try { saved = JSON.parse(raw) } catch { deps.storage.remove(key); return null }
  if (!saved?.clientUploadId) { deps.storage.remove(key); return null }
  if (saved.doneAt && Date.now() - saved.doneAt > VIDEO_DONE_TTL_MS) { deps.storage.remove(key); return null }
  // Admission was sent but its answer never arrived: retry it with the same id (the server is idempotent).
  if (!saved.sessionId) return { pendingClientUploadId: saved.clientUploadId }
  let view: SessionView | null = null
  for (let poll = 0; ; poll++) {
    try {
      view = await deps.api<SessionView>("GET", `${input.basePath}/sessions/${saved.sessionId}`, undefined, input.signal)
    } catch (err) {
      throwIfAborted(input.signal)
      if (isGone(err)) { deps.storage.remove(key); return null }
      throw new VideoUploadError("resume_retry", RETRY_LATER, detailOf(err, "resume"))
    }
    if (view.status !== "reserved" && view.status !== "completing") break
    if (poll >= PENDING_POLLS) throw new VideoUploadError("resume_retry", RETRY_LATER, { phase: "resume", cause: "session_busy" })
    throwIfAborted(input.signal)
    await deps.sleep(PENDING_POLL_MS, input.signal)
    throwIfAborted(input.signal)
  }
  if (DONE_STATUSES.includes(view.status)) return { view, clientUploadId: saved.clientUploadId }
  if (view.status !== "uploading") { deps.storage.remove(key); return null }
  if (view.size !== input.file.size) { deps.storage.remove(key); return null }
  for (const p of view.uploadedParts) {
    throwIfAborted(input.signal)
    const local = await deps.sha256Base64(partBlob(input.file, view, p.n))
    if (!p.sha256 || p.sha256 !== local) {
      await deps.api("POST", `${input.basePath}/sessions/${view.id}/abort`, undefined, input.signal).catch(() => {})
      deps.storage.remove(key)
      return null
    }
  }
  return { view, clientUploadId: saved.clientUploadId }
}

async function putWithRetry(
  deps: VideoUploadDeps, signal: AbortSignal, part: PartUrl, blob: Blob,
  refresh: (n: number) => Promise<PartUrl>, onBytes: (n: number, sent: number) => void,
): Promise<void> {
  let current = part
  for (let attempt = 1; ; attempt++) {
    throwIfAborted(signal)
    try {
      await deps.putPart(current.url, current.headers, blob, (sent) => onBytes(part.n, sent), signal)
      onBytes(part.n, blob.size)
      return
    } catch (err) {
      onBytes(part.n, 0)
      throwIfAborted(signal)
      if (attempt >= PART_ATTEMPTS) {
        throw new VideoUploadError("part_failed", "Verbindung zu schwach. Bitte später erneut versuchen, der Upload setzt dann fort.", { phase: "parts", cause: "network", status: 0 })
      }
      const status = (err as { status?: number })?.status
      if (typeof navigator !== "undefined" && navigator.onLine === false) await deps.waitOnline(signal)
      else await deps.sleep(Math.min(30_000, 1000 * 2 ** (attempt - 1)), signal)
      throwIfAborted(signal)
      // Expired or rejected URL: ask for a fresh one (same checksum). Also after a network error past 2 tries.
      if (status === 403 || attempt >= 2) current = await refresh(part.n)
    }
  }
}

async function uploadParts(deps: VideoUploadDeps, input: VideoUploadInput, view: SessionView, needed: number[], alreadySent: number): Promise<void> {
  const sentByPart = new Map<number, number>()
  const report = () => {
    let sent = alreadySent
    sentByPart.forEach((v) => { sent += v })
    input.onProgress?.({ sentBytes: Math.min(sent, view.size), totalBytes: view.size })
  }
  const onBytes = (n: number, sent: number) => { sentByPart.set(n, sent); report() }
  const checksums = new Map<number, string>()
  const sha = async (n: number) => {
    let v = checksums.get(n)
    if (!v) { v = await deps.sha256Base64(partBlob(input.file, view, n)); checksums.set(n, v) }
    return v
  }
  // One stop signal for every worker of this file: the caller's cancel, or the first part that fails for good.
  const inner = new AbortController()
  const onOuterAbort = () => inner.abort()
  if (input.signal?.aborted) inner.abort()
  else input.signal?.addEventListener("abort", onOuterAbort, { once: true })
  const requestUrls = async (ns: number[]) => {
    // Hashed one after another, never all parts of a batch at once: 20 × 8 MB in memory is too much on a phone.
    const parts: { n: number; sha256: string }[] = []
    for (const n of ns) {
      throwIfAborted(inner.signal)
      parts.push({ n, sha256: await sha(n) })
    }
    throwIfAborted(inner.signal)
    try {
      return (await deps.api<{ parts: PartUrl[] }>("POST", `${input.basePath}/sessions/${view.id}/parts`, { parts }, inner.signal)).parts
    } catch (err) {
      throwIfAborted(inner.signal)
      if (err instanceof VideoUploadError) throw new VideoUploadError(err.code, err.message, detailOf(err, "parts"))
      throw new VideoUploadError("part_failed", "Verbindung zu schwach. Bitte später erneut versuchen, der Upload setzt dann fort.", detailOf(err, "parts"))
    }
  }
  const refresh = async (n: number) => (await requestUrls([n]))[0]

  try {
    for (let i = 0; i < needed.length; i += URL_BATCH) {
      throwIfAborted(inner.signal)
      const batch = needed.slice(i, i + URL_BATCH)
      const urls = await requestUrls(batch)
      const queue = [...urls]
      let firstError: unknown = null
      const workers = Array.from({ length: Math.min(PARALLEL, queue.length) }, async () => {
        for (let next = queue.shift(); next && !inner.signal.aborted; next = queue.shift()) {
          try {
            await putWithRetry(deps, inner.signal, next, partBlob(input.file, view, next.n), refresh, onBytes)
          } catch (err) {
            // First failure stops the siblings; all workers are awaited before the error leaves this file.
            if (firstError === null) firstError = err
            inner.abort()
            return
          }
        }
      })
      await Promise.allSettled(workers)
      if (input.signal?.aborted) throw cancelled()
      if (firstError !== null) throw firstError
    }
  } finally {
    input.signal?.removeEventListener("abort", onOuterAbort)
  }
}

export async function uploadVideo(deps: VideoUploadDeps, input: VideoUploadInput): Promise<VideoUploadResult> {
  const pre = precheckVideo(input.file, input.durationSec)
  if (pre) throw new VideoUploadError("precheck", pre)
  const key = storageKey(input.accessKey, input.file)

  throwIfAborted(input.signal)
  const resumed = await resumeOrForget(deps, input, key)
  let view: SessionView
  let clientUploadId: string
  if (resumed && "view" in resumed) {
    view = resumed.view
    clientUploadId = resumed.clientUploadId
  } else {
    // Remember the id BEFORE admission: if the answer is lost, the retry sends the same id and gets the same
    // session instead of a second one (review video-r1 M4).
    clientUploadId = resumed?.pendingClientUploadId ?? newClientUploadId(deps)
    deps.storage.set(key, JSON.stringify({ clientUploadId } satisfies Remembered))
    throwIfAborted(input.signal)
    try {
      view = await deps.api<SessionView>("POST", `${input.basePath}/sessions`, {
        clientUploadId,
        fileName: input.file.name.slice(0, 200) || "video",
        mimeType: input.file.type,
        size: input.file.size,
        ...(input.durationSec != null ? { durationSec: Math.round(input.durationSec * 10) / 10 } : {}),
        ...(input.message?.trim() ? { message: input.message.trim().slice(0, 2000) } : {}),
        ...(input.pillarName ? { pillarName: input.pillarName } : {}),
        consent: true,
      }, input.signal)
    } catch (err) {
      throwIfAborted(input.signal)
      // Forget the id only on a definitive answer about THIS id: the server checked it and refused (these codes come
      // after the idempotency lookup, or are fixed properties of the file). A 404/410 from a switched-off gate, an
      // auth or rate-limit error, 5xx or offline proves nothing about an earlier admission, so the id stays and the
      // retry finds that session (review video-r2 M3).
      if (DEFINITIVE_REFUSALS.has((err as { code?: string })?.code ?? "")) deps.storage.remove(key)
      if (err instanceof VideoUploadError) throw new VideoUploadError(err.code, err.message, detailOf(err, "admit"))
      throw new VideoUploadError("admit_retry", RETRY_LATER, detailOf(err, "admit"))
    }
    deps.storage.set(key, JSON.stringify({ sessionId: view.id, clientUploadId } satisfies Remembered))
  }

  const alreadyDone = DONE_STATUSES.includes(view.status) && !!resumed && "view" in resumed
  for (let round = 0; round < COMPLETE_ROUNDS; round++) {
    if (DONE_STATUSES.includes(view.status)) break
    if (view.status !== "uploading") throw new VideoUploadError(view.errorCode ?? "failed", "Der Upload konnte nicht abgeschlossen werden. Bitte erneut hochladen.")
    const have = new Set(view.uploadedParts.map((p) => p.n))
    const missing = view.missingParts ?? Array.from({ length: view.partCount }, (_, i) => i + 1).filter((n) => !have.has(n))
    const alreadySent = view.uploadedParts.filter((p) => !missing.includes(p.n)).reduce((s, p) => s + p.size, 0)
    input.onProgress?.({ sentBytes: alreadySent, totalBytes: view.size })
    if (missing.length) await uploadParts(deps, input, view, missing, alreadySent)
    throwIfAborted(input.signal)
    try {
      view = await deps.api<SessionView>("POST", `${input.basePath}/sessions/${view.id}/complete`, undefined, input.signal)
    } catch (err) {
      // A complete that reached the server may still finish there; the pointer stays, the next try finds it.
      throwIfAborted(input.signal)
      if (err instanceof VideoUploadError) throw new VideoUploadError(err.code, err.message, detailOf(err, "complete"))
      throw new VideoUploadError("complete_retry", RETRY_LATER, detailOf(err, "complete"))
    }
  }
  if (!DONE_STATUSES.includes(view.status)) {
    throw new VideoUploadError(view.errorCode ?? "incomplete", "Der Upload konnte nicht abgeschlossen werden. Bitte erneut versuchen, er setzt dann fort.")
  }
  // One write replaces the pointer with the finished marker; if it fails the old pointer still leads to this
  // session, so a second pick never admits a duplicate (review multivideo-r2 M2).
  deps.storage.set(key, JSON.stringify({ sessionId: view.id, clientUploadId, doneAt: Date.now() } satisfies Remembered))
  input.onProgress?.({ sentBytes: view.size, totalBytes: view.size })
  return { sessionId: view.id, status: view.status, alreadyDone }
}

/** Forget the remembered session of this file (user chose "upload anyway"). */
export function forgetVideo(deps: Pick<VideoUploadDeps, "storage">, accessKey: string, file: File): void {
  deps.storage.remove(storageKey(accessKey, file))
}

/** Drop finished markers older than 24 h for this access (keeps localStorage small). */
export function pruneDoneVideos(deps: Pick<VideoUploadDeps, "storage">, accessKey: string, now = Date.now()): void {
  const prefix = `hotel-video:${accessKey}:`
  for (const k of deps.storage.keys?.() ?? []) {
    if (!k.startsWith(prefix)) continue
    try {
      const saved = JSON.parse(deps.storage.get(k) ?? "null") as Remembered | null
      if (saved?.doneAt && now - saved.doneAt > VIDEO_DONE_TTL_MS) deps.storage.remove(k)
    } catch { /* not ours to judge */ }
  }
}

export type DiscardOutcome =
  /** Server confirmed aborted (or the session no longer exists): place freed, pointer gone. */
  | "discarded"
  /** The upload finished meanwhile: shown as arrived, pointer kept as finished. */
  | "arrived"
  /** Server is still settling the session (reserved/completing/409): try again shortly, pointer kept. */
  | "busy"
  /** Network, rate limit or server error: pointer kept, the button stays. */
  | "retry"
  /** No session id known (admission answer lost): only "try again" can resolve it. */
  | "unknown"

/** "Verwerfen" for a failed file (PLAN-multivideo.md §11a): idempotent; the pointer goes only after a confirmed end. */
export async function discardVideo(deps: VideoUploadDeps, input: Pick<VideoUploadInput, "file" | "basePath" | "accessKey">): Promise<DiscardOutcome> {
  const key = storageKey(input.accessKey, input.file)
  let saved: Remembered | null = null
  try { saved = JSON.parse(deps.storage.get(key) ?? "null") } catch { saved = null }
  if (!saved?.clientUploadId) return "discarded"
  if (!saved.sessionId) return "unknown"
  const path = `${input.basePath}/sessions/${saved.sessionId}`
  let view: SessionView
  try {
    view = await deps.api<SessionView>("GET", path)
  } catch (err) {
    if (isGone(err)) { deps.storage.remove(key); return "discarded" }
    return "retry"
  }
  if (DONE_STATUSES.includes(view.status)) {
    deps.storage.set(key, JSON.stringify({ sessionId: view.id, clientUploadId: saved.clientUploadId, doneAt: saved.doneAt ?? Date.now() } satisfies Remembered))
    return "arrived"
  }
  if (view.status === "reserved" || view.status === "completing") return "busy"
  if (view.status !== "uploading") { deps.storage.remove(key); return "discarded" }
  try {
    const after = await deps.api<SessionView>("POST", `${path}/abort`)
    if (after.status === "aborted") { deps.storage.remove(key); return "discarded" }
    return "busy"
  } catch (err) {
    if (isGone(err)) { deps.storage.remove(key); return "discarded" }
    const code = (err as { code?: string })?.code
    if (code === "not_uploading" || code === "session_changed") return "busy"
    return "retry"
  }
}

// ── browser implementations ─────────────────────────────────────────────────────────────────────

function safeStorage(): VideoUploadDeps["storage"] {
  return {
    get: (k) => { try { return window.localStorage.getItem(k) } catch { return null } },
    set: (k, v) => { try { window.localStorage.setItem(k, v) } catch { /* private mode: no resume */ } },
    remove: (k) => { try { window.localStorage.removeItem(k) } catch { /* ignore */ } },
    keys: () => { try { return Object.keys(window.localStorage) } catch { return [] } },
  }
}

/** Seconds from Retry-After (seconds or HTTP date) or RateLimit-Reset; undefined when neither is usable. */
export function retryAfterSeconds(headers: { get(name: string): string | null }): number | undefined {
  const retryAfter = headers.get("Retry-After")
  if (retryAfter) {
    const n = Number(retryAfter)
    if (Number.isFinite(n) && n >= 0) return Math.ceil(n)
    const date = Date.parse(retryAfter)
    if (!Number.isNaN(date)) return Math.max(0, Math.ceil((date - Date.now()) / 1000))
  }
  const reset = headers.get("RateLimit-Reset") ?? headers.get("X-RateLimit-Reset")
  const n = reset ? Number(reset) : NaN
  return Number.isFinite(n) && n >= 0 ? Math.ceil(n) : undefined
}

async function browserApi<T>(method: "GET" | "POST", path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : (method === "POST" ? "{}" : undefined),
      signal,
    })
  } catch {
    if (signal?.aborted) throw cancelled()
    throw new VideoUploadError("network", RETRY_LATER, { status: 0 })
  }
  let data: any = null
  try { data = await res.json() } catch { /* empty */ }
  if (!res.ok) {
    throw new VideoUploadError(data?.code ?? `http_${res.status}`, data?.error ?? "Upload fehlgeschlagen.", {
      status: res.status, retryAfterSec: retryAfterSeconds(res.headers),
    })
  }
  return data as T
}

function browserPutPart(url: string, headers: Record<string, string>, body: Blob, onBytes: (sent: number) => void, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", url)
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v)
    xhr.upload.onprogress = (e) => onBytes(e.loaded)
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject({ status: xhr.status }))
    xhr.onerror = () => reject({ status: 0 })
    xhr.ontimeout = () => reject({ status: 0 })
    xhr.timeout = 5 * 60_000
    signal?.addEventListener("abort", () => xhr.abort(), { once: true })
    xhr.onabort = () => reject({ status: 0, aborted: true })
    xhr.send(body)
  })
}

async function browserSha256Base64(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer())
  let bin = ""
  const bytes = new Uint8Array(digest)
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((r) => {
    if (signal?.aborted) return r()
    const done = () => { clearTimeout(t); signal?.removeEventListener("abort", done); r() }
    const t = setTimeout(done, ms)
    signal?.addEventListener("abort", done, { once: true })
  })
}

export function browserVideoUploadDeps(): VideoUploadDeps {
  return {
    api: browserApi,
    putPart: browserPutPart,
    storage: safeStorage(),
    sha256Base64: browserSha256Base64,
    uuid: () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`),
    sleep: abortableSleep,
    waitOnline: (signal) => new Promise((r) => {
      if (signal?.aborted) return r()
      const done = () => { window.removeEventListener("online", done); signal?.removeEventListener("abort", done); r() }
      window.addEventListener("online", done, { once: true })
      signal?.addEventListener("abort", done, { once: true })
    }),
  }
}

/** Duration from the file's metadata; null when the browser cannot read it (e.g. HEVC on desktop). */
export function readVideoDuration(file: File, timeoutMs = 8000): Promise<number | null> {
  return new Promise((resolve) => {
    let url = ""
    try { url = URL.createObjectURL(file) } catch { resolve(null); return }
    const v = document.createElement("video")
    const done = (d: number | null) => { clearTimeout(t); URL.revokeObjectURL(url); resolve(d) }
    const t = setTimeout(() => done(null), timeoutMs)
    v.preload = "metadata"
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : null)
    v.onerror = () => done(null)
    v.src = url
  })
}
